import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";

/**
 * Public form to capture leads for the ₹8,000 fully-custom wedding website service.
 * Includes simple math captcha + 60s rate-limit (enforced server-side).
 */
export default function CustomLeadForm({ phone = "9936120357" }) {
  const [form, setForm] = useState({ name: "", phone: "", email: "", wedding_date: "", preferred_call_time: "", notes: "" });
  const [captcha, setCaptcha] = useState({ a: 0, b: 0 });
  const [captchaAnswer, setCaptchaAnswer] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const regen = () => {
    setCaptcha({ a: Math.floor(Math.random() * 8) + 2, b: Math.floor(Math.random() * 8) + 2 });
    setCaptchaAnswer("");
  };
  useEffect(() => { regen(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      return toast.error("Please add your name and phone number");
    }
    const ans = parseInt(captchaAnswer, 10);
    if (Number.isNaN(ans)) return toast.error("Please answer the verification sum");

    setBusy(true);
    try {
      await api.post("/leads/custom", {
        ...form,
        captcha_a: captcha.a,
        captcha_b: captcha.b,
        captcha_answer: ans,
      });
      setDone(true);
      toast.success("Thanks! Our team will call you within 1 business hour.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit. Try calling +91 9936120357.");
      regen();
    } finally {
      setBusy(false);
    }
  };

  const fld = "w-full min-h-[48px] px-4 py-3 bg-white border-2 border-stone-300 outline-none focus:border-stone-900 transition-colors";

  if (done) {
    return (
      <div className="bg-emerald-50 border-2 border-emerald-200 p-8 text-center" data-testid="custom-lead-thanks">
        <div className="text-5xl mb-3">✓</div>
        <div className="font-serif-display text-3xl mb-2">Got it. Thanks!</div>
        <p className="text-stone-700">Our team will call you back within <strong>1 business hour</strong> to schedule a design meeting. Meanwhile, you can also reach us directly:</p>
        <div className="mt-4 flex flex-wrap justify-center gap-2">
          <a href={`tel:+91${phone}`} className="px-5 py-2.5 bg-stone-900 text-white text-xs uppercase tracking-[0.3em] hover:bg-stone-800">📞 +91 {phone}</a>
        </div>
        <button onClick={() => { setDone(false); setForm({ name: "", phone: "", email: "", wedding_date: "", preferred_call_time: "", notes: "" }); regen(); }} className="mt-6 text-xs uppercase tracking-[0.3em] underline underline-offset-4">Submit another</button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="custom-lead-form">
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-[10px] uppercase tracking-[0.3em] mb-1.5 text-stone-500">Your name *</label>
          <input data-testid="lead-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Aarav Kapoor" className={fld} />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-[0.3em] mb-1.5 text-stone-500">Phone *</label>
          <input data-testid="lead-phone" required type="tel" inputMode="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+91 98765 43210" className={fld} />
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-[10px] uppercase tracking-[0.3em] mb-1.5 text-stone-500">Email (optional)</label>
          <input data-testid="lead-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@example.com" className={fld} />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-[0.3em] mb-1.5 text-stone-500">Wedding date</label>
          <input data-testid="lead-date" type="text" value={form.wedding_date} onChange={(e) => setForm({ ...form, wedding_date: e.target.value })} placeholder="12 Mar 2027 (or tentative)" className={fld} />
        </div>
      </div>
      <div>
        <label className="block text-[10px] uppercase tracking-[0.3em] mb-1.5 text-stone-500">When can we call you?</label>
        <input data-testid="lead-time" value={form.preferred_call_time} onChange={(e) => setForm({ ...form, preferred_call_time: e.target.value })} placeholder="Today after 6pm / tomorrow morning" className={fld} />
      </div>
      <div>
        <label className="block text-[10px] uppercase tracking-[0.3em] mb-1.5 text-stone-500">What kind of website do you want? (optional)</label>
        <textarea data-testid="lead-notes" rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Royal theme · save-the-date video · live RSVP · 200 guests · custom subdomain..." className={fld + " min-h-[100px]"} />
      </div>

      {/* Captcha */}
      <div className="bg-stone-50 border border-stone-300 p-3" data-testid="lead-captcha">
        <div className="text-[10px] uppercase tracking-[0.3em] text-stone-500 mb-1">Verification</div>
        <label className="flex items-center gap-3">
          <span className="font-serif-display text-lg">{captcha.a} + {captcha.b} =</span>
          <input data-testid="lead-captcha-input" type="number" inputMode="numeric" required value={captchaAnswer} onChange={(e) => setCaptchaAnswer(e.target.value)} placeholder="?" className="flex-1 px-3 py-2 border border-stone-300 text-center focus:outline-none focus:border-stone-900" />
        </label>
      </div>

      <button type="submit" disabled={busy} data-testid="lead-submit" className="w-full min-h-[56px] px-8 py-4 bg-amber-500 text-stone-900 uppercase tracking-[0.35em] text-sm font-semibold transition-all hover:bg-amber-400 disabled:opacity-60 shadow-md">
        {busy ? "Sending…" : "★ Schedule My Design Meeting"}
      </button>
      <div className="text-[10px] text-stone-500 text-center">No payment now · We'll call back within 1 business hour</div>
    </form>
  );
}
